import React from "react";
import Alert from "@mui/material/Alert";
import type { AlertProps } from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import IconButton from "@mui/material/IconButton";
import { Icon } from "../../atoms/Icon";
import type { BaseMoleculeProps } from "../../types";

export interface MessageProps
  extends Omit<AlertProps, "variant">,
    Omit<BaseMoleculeProps, "variant"> {
  /**
   * Message severity
   */
  severity?: "success" | "info" | "warning" | "error";

  /**
   * Message variant
   */
  variant?: "default" | "compact" | "expanded" | "filled" | "outlined";

  /**
   * Message title
   */
  title?: string;

  /**
   * Show close button
   */
  closable?: boolean;

  /**
   * Close button handler
   */
  onClose?: () => void;

  /**
   * Auto-hide duration in milliseconds
   */
  autoHideDuration?: number;

  /**
   * Custom icon
   */
  icon?: React.ReactNode;

  /**
   * Hide default icon
   */
  hideIcon?: boolean;

  /**
   * Loading state
   */
  loading?: boolean;
}

const MessageComponent: React.FC<MessageProps> = ({
  severity = "info",
  variant = "default",
  title,
  closable = false,
  onClose,
  autoHideDuration,
  icon,
  hideIcon = false,
  loading = false,
  className,
  "data-testid": _dataTestId,
  children,
  ...props
}) => {
  // Auto-hide functionality
  React.useEffect(() => {
    if (autoHideDuration && onClose) {
      const timer = setTimeout(onClose, autoHideDuration);
      return () => clearTimeout(timer);
    }
  }, [autoHideDuration, onClose]);

  const alertVariant: AlertProps["variant"] =
    variant === "filled"
      ? "filled"
      : variant === "outlined"
        ? "outlined"
        : "standard";

  const alertProps: AlertProps = {
    severity,
    variant: alertVariant,
    icon: hideIcon ? false : icon,
    className,
    sx: {
      // Mobile-first responsive styling
      fontSize: {
        xs: "0.875rem", // Mobile
        md: "1rem", // Desktop
      },
      padding: {
        xs: variant === "compact" ? "8px 12px" : "12px 16px",
        md:
          variant === "compact"
            ? "10px 16px"
            : variant === "expanded"
              ? "20px 24px"
              : "16px 20px",
      },
      borderRadius: {
        xs: "6px", // Mobile
        md: "8px", // Desktop
      },
      // Loading state
      ...(loading && {
        opacity: 0.7,
        pointerEvents: "none",
      }),
      // Action area for close button
      "& .MuiAlert-action": {
        alignItems: "flex-start",
        paddingTop: {
          xs: "4px",
          md: "6px",
        },
      },
    },
    action: closable ? (
      <IconButton
        size="small"
        onClick={onClose}
        sx={{
          minWidth: "32px",
          minHeight: "32px",
          color: "inherit",
        }}
      >
        <Icon>
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
        </Icon>
      </IconButton>
    ) : undefined,
    ...props,
  };

  return (
    <Alert {...alertProps}>
      {title && (
        <AlertTitle
          sx={{
            fontSize: {
              xs: "1rem", // Mobile
              md: "1.125rem", // Desktop
            },
            fontWeight: 600,
            marginBottom: title && children ? 1 : 0,
          }}
        >
          {title}
        </AlertTitle>
      )}
      {children}
    </Alert>
  );
};

// Apply memoization with React.memo
export const Message = React.memo(MessageComponent);

// Set display name for debugging
(Message as any).displayName = "Message";
